<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  responsible: { type: String, required: true },
  description: { type: String, required: true },
  allowedStates: { type: Array, default: () => [] },
});
const emit = defineEmits(['confirm', 'close']);
const { t } = useI18n();
const dialog = ref(null);
const initialStatus = ref('Backlog');

const stateOptions = [
  { value: 'Backlog', key: 'BACKLOG', icon: 'i-lucide-inbox', disabled: false },
  {
    value: 'Triagem',
    key: 'TRIAGE',
    icon: 'i-lucide-list-filter',
    disabled: true,
  },
  {
    value: 'Em Progresso',
    key: 'RESOLUTION',
    icon: 'i-lucide-play',
    disabled: true,
  },
];
const initialStates = computed(() =>
  stateOptions.map(state => ({
    ...state,
    disabled: props.allowedStates.length
      ? !props.allowedStates.some(allowed => allowed.value === state.value)
      : state.disabled,
  }))
);
const canConfirm = computed(() =>
  initialStates.value.some(
    state => state.value === initialStatus.value && !state.disabled
  )
);
const confirm = () => {
  if (canConfirm.value) emit('confirm', initialStatus.value);
};

onMounted(() => dialog.value.open());
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('GG_SUPPORT.CREATION.CONFIRMATION.TITLE')"
    :description="t('GG_SUPPORT.CREATION.CONFIRMATION.DESCRIPTION')"
    :cancel-button-label="t('GG_SUPPORT.CREATION.CONFIRMATION.BACK')"
    :confirm-button-label="t('GG_SUPPORT.CREATION.CONFIRMATION.CONFIRM')"
    :disable-confirm-button="!canConfirm"
    overflow-y-auto
    @confirm="confirm"
    @close="emit('close')"
  >
    <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
      <legend class="mb-2 text-sm font-medium text-n-slate-12">
        {{ t('GG_SUPPORT.CREATION.CONFIRMATION.INITIAL_STATUS') }}
      </legend>
      <label
        v-for="state in initialStates"
        :key="state.value"
        class="flex items-center gap-3 rounded-lg border p-3"
        :class="[
          state.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          initialStatus === state.value
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak bg-n-alpha-1',
        ]"
      >
        <input
          v-model="initialStatus"
          type="radio"
          name="gg-support-initial-status"
          :value="state.value"
          :disabled="state.disabled"
          :aria-describedby="`gg-support-initial-${state.key}-hint`"
          class="m-0 shrink-0 accent-n-brand"
        />
        <Icon
          :icon="state.icon"
          class="size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
        <span class="flex min-w-0 flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t(`GG_SUPPORT.CREATION.CONFIRMATION.${state.key}.LABEL`) }}
          </span>
          <span
            :id="`gg-support-initial-${state.key}-hint`"
            class="text-xs text-n-slate-11"
          >
            {{ t(`GG_SUPPORT.CREATION.CONFIRMATION.${state.key}.HINT`) }}
          </span>
        </span>
      </label>
      <p
        v-if="!allowedStates.length"
        role="note"
        class="m-0 text-xs text-n-slate-11"
      >
        {{ t('GG_SUPPORT.CREATION.CONFIRMATION.STATUS_PENDING') }}
      </p>
      <p v-else role="note" class="m-0 text-xs text-n-slate-11">
        {{ t('GG_SUPPORT.CREATION.CONFIRMATION.ADDITIONAL_BACKLOG') }}
      </p>
    </fieldset>
    <div class="flex flex-col gap-3 rounded-lg bg-n-alpha-2 p-3">
      <div class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">
          {{ t('GG_SUPPORT.RESPONSIBLE') }}
        </span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ responsible }}
        </span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">
          {{ t('GG_SUPPORT.CREATION.DESCRIPTION') }}
        </span>
        <p
          class="m-0 max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-sm text-n-slate-12"
        >
          {{ description }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
